import React from 'react';
import { Box, Container, Typography, Grid, Button, Card, CardHeader, CardContent, Chip, Stack } from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import ForumIcon from '@mui/icons-material/Forum';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';

const Section = ({ id, children, sx = {} }) => (
  <Box id={id} sx={{ py: { xs: 6, md: 8 }, ...sx }}>
    <Container maxWidth="lg">{children}</Container>
  </Box>
);

const Item = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <CheckCircle sx={{ fontSize: 18, mt: '2px', color: 'success.main' }} />
    <Typography variant="body2">{text}</Typography>
  </Box>
);

export default function ParentalEngagement() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* HERO */}
      <Section>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Chip label="Families first" color="primary" variant="outlined" sx={{ borderRadius: 2 }} />
            <Typography variant="h3" fontWeight={700} sx={{ mt: 2 }}>Parental Engagement</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              We equip families with actionable insights and tools to raise future‑ready learners. Our programs combine multi‑channel communication, curated resources, and expert‑led sessions—covering child development, well‑being, and digital citizenship—to build a strong, informed school‑parent community.
            </Typography>
            <Stack direction="row" spacing={1.5} sx={{ mt: 3, flexWrap: 'wrap' }}>
              <Button variant="contained" endIcon={<ArrowRightAlt />}>Partner with us</Button>
              <Button variant="outlined">Explore programs</Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader title="What parents get" />
              <CardContent sx={{ display: 'grid', gap: 1.25 }}>
                <Item text="Timely updates via WhatsApp, email, and in‑app" />
                <Item text="Curated guides on child development and well‑being" />
                <Item text="Expert webinars and Q&A sessions" />
                <Item text="Practical toolkits for digital citizenship at home" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>

      {/* FEATURES */}
      <Section>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader avatar={<ForumIcon color="primary" />} title="Multi‑channel communication" />
              <CardContent>
                <Typography variant="body2" color="text.secondary">Reach parents where they are—WhatsApp, SMS, and email—for better engagement.</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader avatar={<LibraryBooksIcon color="primary" />} title="Curated resources" />
              <CardContent>
                <Typography variant="body2" color="text.secondary">Age‑appropriate guides, checklists, and activities aligned to learning goals.</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader avatar={<FamilyRestroomIcon color="primary" />} title="Expert‑led sessions" />
              <CardContent>
                <Typography variant="body2" color="text.secondary">Interactive webinars and workshops with educators, psychologists, and mentors.</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Section>
    </Box>
  );
}
